import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../../api/api";
import { CreateNoteModal } from "../../components/CreateNoteModal";
import { useDialog } from "../../components/DialogProvider";
import { PackageSearch, AlertTriangle, PlusCircle, Store, Calendar, ChevronRight, ImageOff } from "lucide-react";

function ProductIcon({ imageUrl, alt, sizeClass = "h-10 w-10" }) {
  const [broken, setBroken] = useState(false);
  const apiBase = process.env.REACT_APP_API_BASE_URL || "";
  const src = useMemo(() => {
    if (!imageUrl) return "";
    const raw = String(imageUrl);
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    return `${apiBase}${raw}`;
  }, [apiBase, imageUrl]);

  if (!src || broken) {
    return (
      <div className={`flex shrink-0 items-center justify-center rounded-md border border-zinc-200 bg-zinc-50 text-zinc-400 ${sizeClass}`}>
        <ImageOff className="h-4 w-4" />
      </div>
    );
  }
  return (
    <img 
      src={src} 
      alt={alt} 
      className={`shrink-0 rounded-md border border-zinc-200 object-cover ${sizeClass}`} 
      loading="lazy" 
      onError={() => setBroken(true)} 
    />
  );
}

export default function AccountantStock() {
  const { toast } = useDialog();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Tabs: 'warehouse' or 'events'
  const [activeTab, setActiveTab] = useState('warehouse');

  // Modal Note State
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteRefId, setNoteRefId] = useState(null);

  // Accordion state for events
  const [expandedEvents, setExpandedEvents] = useState({});

  const fetchStockOverview = useCallback(async () => {
    try {
      const res = await api.get("/accountant/stock-overview");
      setData(res.data);
      
      // Auto-expand all events by default
      if (res.data?.events) {
        const eventsObj = {};
        res.data.events.forEach(row => {
          eventsObj[row.event_name] = true;
        });
        setExpandedEvents(eventsObj);
      }
    } catch (err) {
      toast({ type: "error", title: "Error", message: "Gagal memuat ringkasan stok." });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchStockOverview();
  }, [fetchStockOverview]);

  const openNoteModal = (productId) => {
    setNoteRefId(productId);
    setIsNoteModalOpen(true);
  };

  const toggleEvent = (eventName) => {
    setExpandedEvents(prev => ({
      ...prev,
      [eventName]: !prev[eventName]
    }));
  };

  // Group events data
  const groupedEvents = useMemo(() => {
    if (!data?.events) return {};
    return data.events.reduce((acc, curr) => {
      if (!acc[curr.event_name]) {
        acc[curr.event_name] = {
          status: curr.event_status,
          items: []
        };
      }
      acc[curr.event_name].items.push(curr);
      return acc;
    }, {});
  }, [data?.events]);

  return (
    <div className="space-y-6 animate-fade-in pb-10 max-w-[1400px]">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Pantau Stok</h1>
        <p className="mt-1 text-sm text-zinc-500">Laporan lengkap stok gudang utama dan pergerakan barang secara real-time.</p>
      </header>

      {loading ? (
        <div className="flex justify-center p-12">
          <div className="flex items-center gap-2 text-zinc-400 text-sm">
            <PackageSearch className="h-4 w-4" /> Memuat perhitungan stok...
          </div>
        </div>
      ) : !data ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-6 text-center text-sm text-rose-600">Gagal memuat data stok.</div>
      ) : (
        <div className="space-y-6">
          
          {/* Alert: Selisih Opname Terbaru */}
          {data.stock_discrepancies.length > 0 && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-5 shadow-sm">
              <h3 className="flex items-center gap-2 text-rose-800 font-semibold mb-4 text-sm">
                <AlertTriangle className="h-4 w-4" />
                Selisih Stok Fisik (Butuh Perhatian Khusus)
              </h3>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {data.stock_discrepancies.slice(0, 4).map((opname, idx) => (
                  <div key={idx} className="rounded-md border border-rose-200 bg-white p-3 flex flex-col justify-between">
                    <div>
                      <div className="font-mono text-[11px] font-bold text-zinc-700">{opname.kode_barang}</div>
                      <div className="mt-1 flex items-center justify-between">
                        <span className="text-[11px] text-zinc-500 uppercase tracking-wider">Selisih:</span>
                        <span className="font-bold text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded text-[11px]">{opname.selisih} pcs</span>
                      </div>
                    </div>
                    {opname.keterangan && (
                      <div className="mt-3 text-[11px] text-zinc-600 border-t border-zinc-100 pt-2 line-clamp-2">
                        {opname.keterangan}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Navigation Tabs */}
          <div className="flex gap-2 border-b border-zinc-200">
            <button
              className={`flex items-center gap-2 px-4 py-2.5 text-[13px] font-semibold transition-all border-b-2 mb-[-1px] ${
                activeTab === "warehouse" ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300"
              }`}
              onClick={() => setActiveTab("warehouse")}
            >
              <Store className="h-4 w-4" /> Gudang Utama
            </button>
            <button
              className={`flex items-center gap-2 px-4 py-2.5 text-[13px] font-semibold transition-all border-b-2 mb-[-1px] ${
                activeTab === "events" ? "border-zinc-900 text-zinc-900" : "border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300"
              }`}
              onClick={() => setActiveTab("events")}
            >
              <Calendar className="h-4 w-4" /> Stok Berjalan (Event)
            </button>
          </div>

          {/* Tab Content: Gudang Utama */}
          {activeTab === "warehouse" && (
            <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden shadow-sm animate-fade-in">
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-[13px]">
                  <thead className="bg-zinc-50 border-b border-zinc-200 text-zinc-600">
                    <tr>
                      <th className="px-4 py-3 font-medium w-12">Produk</th>
                      <th className="px-4 py-3 font-medium">Detail</th>
                      <th className="px-4 py-3 font-medium text-right">Total Masuk</th>
                      <th className="px-4 py-3 font-medium text-right">Total Keluar</th>
                      <th className="px-4 py-3 font-medium text-right">Sisa Sistem</th>
                      <th className="px-4 py-3 font-medium text-center">Tindakan</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {data.warehouse.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-12 text-center text-zinc-400">Tidak ada data produk di gudang.</td></tr>
                    ) : (
                      data.warehouse.map((row) => (
                        <tr key={row.id} className="hover:bg-zinc-50/50 transition-colors bg-white">
                          <td className="px-4 py-3">
                            <ProductIcon imageUrl={row.image_url} alt={row.nama_barang} />
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-zinc-900 line-clamp-1">{row.nama_barang}</div>
                            <div className="font-mono text-[11px] text-zinc-500 mt-0.5">{row.kode_barang}</div>
                          </td>
                          <td className="px-4 py-3 text-right text-zinc-600 tabular-nums">{row.total_stock_in}</td>
                          <td className="px-4 py-3 text-right text-zinc-600 tabular-nums">{row.total_stock_out}</td>
                          <td className="px-4 py-3 text-right">
                            <span className="inline-flex min-w-[3rem] items-center justify-center rounded-md bg-zinc-100 px-2 py-1 text-[13px] font-bold text-zinc-900 border border-zinc-200 tabular-nums">
                              {row.current_stock}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => openNoteModal(row.id)}
                              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-[11px] font-medium text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors"
                            >
                              <PlusCircle className="h-3.5 w-3.5" /> Lapor Masalah
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Tab Content: Stok Event */}
          {activeTab === "events" && (
            <div className="space-y-4 animate-fade-in">
              {Object.keys(groupedEvents).length === 0 ? (
                <div className="rounded-lg border border-zinc-200 bg-white p-12 text-center text-zinc-400 text-sm">
                  Belum ada stok yang sedang berjalan di event.
                </div>
              ) : (
                Object.entries(groupedEvents).map(([eventName, eventData]) => {
                  const isExpanded = expandedEvents[eventName];
                  return (
                    <div key={eventName} className="rounded-lg border border-zinc-200 bg-white shadow-sm overflow-hidden transition-all duration-200">
                      {/* Event Header */}
                      <div 
                        className={`flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-zinc-50/50 border-l-4 ${eventData.status === 'active' ? 'border-l-emerald-500' : 'border-l-zinc-300'}`}
                        onClick={() => toggleEvent(eventName)}
                      >
                        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                          <h3 className="text-sm font-bold text-zinc-900">{eventName}</h3>
                          <span className={`inline-flex w-fit items-center rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ring-inset ${
                            eventData.status === 'active' ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20' : 'bg-zinc-50 text-zinc-600 ring-zinc-500/20'
                          }`}>
                            {eventData.status === 'active' ? 'Event Berjalan' : 'Event Selesai'}
                          </span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-[11px] font-medium text-zinc-500">
                            {eventData.items.length} Produk
                          </span>
                          <button type="button" className={`inline-flex h-6 w-6 items-center justify-center rounded border ${isExpanded ? 'border-zinc-300 bg-zinc-100 text-zinc-700' : 'border-transparent text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600'} transition-all`}>
                            <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`} />
                          </button>
                        </div>
                      </div>

                      {/* Event Items */}
                      {isExpanded && (
                        <div className="border-t border-zinc-200 bg-zinc-50/50 p-4">
                          <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm">
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-left text-[13px]">
                                <thead className="bg-white border-b border-zinc-200 text-zinc-600">
                                  <tr>
                                    <th className="px-4 py-3 font-medium w-12">Produk</th>
                                    <th className="px-4 py-3 font-medium">Detail</th>
                                    <th className="px-4 py-3 font-medium text-right w-24">Dikirim</th>
                                    <th className="px-4 py-3 font-medium text-right w-24">Terjual</th>
                                    <th className="px-4 py-3 font-medium text-right w-28">Sisa Sistem</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-zinc-100">
                                  {eventData.items.map((item, idx) => (
                                    <tr key={idx} className="hover:bg-zinc-50/50 transition-colors bg-white">
                                      <td className="px-4 py-2.5">
                                        <ProductIcon imageUrl={item.image_url} alt={item.nama_barang} />
                                      </td>
                                      <td className="px-4 py-2.5">
                                        <div className="font-semibold text-zinc-900 line-clamp-1">{item.nama_barang}</div>
                                        <div className="font-mono text-[11px] text-zinc-500 mt-0.5">{item.kode_barang}</div>
                                      </td>
                                      <td className="px-4 py-2.5 text-right font-medium text-zinc-600 tabular-nums">{item.qty_transferred}</td>
                                      <td className="px-4 py-2.5 text-right font-bold text-emerald-600 tabular-nums">{item.qty_sold}</td>
                                      <td className="px-4 py-2.5 text-right">
                                        <span className="inline-flex min-w-[2.5rem] items-center justify-center rounded-md border border-zinc-200 bg-zinc-100 px-1.5 py-0.5 text-[13px] font-bold text-zinc-900 tabular-nums">
                                          {item.qty_remaining}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

        </div>
      )}

      {/* Note Modal */}
      <CreateNoteModal
        open={isNoteModalOpen}
        onClose={() => setIsNoteModalOpen(false)}
        referenceType="stock"
        referenceId={noteRefId}
      />
    </div>
  );
}
